import { QueryClient, QueryFunction } from "@tanstack/react-query";

interface ClerkSession {
  getToken: () => Promise<string | null>;
}
interface ClerkGlobal {
  session?: ClerkSession | null;
}
declare global {
  interface Window {
    Clerk?: ClerkGlobal;
  }
}

async function getClerkToken(): Promise<string | null> {
  try {
    const session = window.Clerk?.session;
    if (session?.getToken) {
      return await session.getToken();
    }
  } catch {
    // ignore
  }
  return null;
}

async function buildHeaders(extra?: Record<string, string>): Promise<Record<string, string>> {
  const headers: Record<string, string> = { ...(extra || {}) };
  const token = await getClerkToken();
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

// ─── Structured API error ────────────────────────────────────────────────────

export interface ApiErrorDetail {
  path: string;
  message: string;
  code?: string;
}

/**
 * Thrown by `throwIfResNotOk` and `apiRequest` when the server returns a
 * non-2xx response. Carries the full structured error envelope so callers can
 * surface field-level hints (`details`) and copy a reference ID (`requestId`)
 * when reporting unexpected failures.
 *
 * `rawBody` holds the complete parsed JSON object from the response so callers
 * can inspect server-specific fields (e.g. `action`, `reasons`, `errors`)
 * without having to re-fetch or switch away from `apiRequest`.
 */
export class ApiError extends Error {
  status: number;
  code: string;
  requestId: string | undefined;
  details: ApiErrorDetail[] | undefined;
  rawBody: Record<string, unknown> | undefined;

  constructor(
    status: number,
    message: string,
    code: string,
    requestId?: string,
    details?: ApiErrorDetail[],
    rawBody?: Record<string, unknown>,
  ) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.requestId = requestId;
    this.details = details;
    this.rawBody = rawBody;
  }
}

/** Return the field-level error message for a given dot-notation path, if any. */
export function getFieldError(error: unknown, path: string): string | undefined {
  if (error instanceof ApiError && Array.isArray(error.details)) {
    const hit = error.details.find((d) => d.path === path);
    return hit?.message;
  }
  return undefined;
}

/** Return true when the error is a server-side validation error (VALIDATION_ERROR code). */
export function isValidationError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.code === "VALIDATION_ERROR";
}

// ─── Core fetch helpers ──────────────────────────────────────────────────────

async function throwIfResNotOk(res: Response): Promise<void> {
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let body: unknown;
    try {
      body = JSON.parse(text);
    } catch {
      throw new ApiError(res.status, text || res.statusText, "ERROR");
    }

    if (body && typeof body === "object") {
      const b = body as Record<string, unknown>;
      // Prefer the structured envelope `{ error: { code, message, requestId, details? } }`
      const envelope =
        b.error !== null && typeof b.error === "object"
          ? (b.error as Record<string, unknown>)
          : null;
      if (envelope) {
        const message =
          typeof envelope.message === "string" ? envelope.message : res.statusText;
        const code =
          typeof envelope.code === "string" ? envelope.code : "ERROR";
        const requestId =
          typeof envelope.requestId === "string" ? envelope.requestId : undefined;
        const rawDetails = envelope.details;
        const details = Array.isArray(rawDetails)
          ? (rawDetails as ApiErrorDetail[])
          : undefined;
        // Pass full body as rawBody so callers can access server-specific fields
        // (e.g. action, reasons, errors) without re-fetching.
        throw new ApiError(res.status, message, code, requestId, details, b);
      }
      // Fallback: bare `{ message }` shape (kept for safety)
      const message =
        typeof b.message === "string" ? b.message : res.statusText;
      throw new ApiError(res.status, message, "ERROR", undefined, undefined, b);
    }

    throw new ApiError(res.status, text || res.statusText, "ERROR");
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  const headers = await buildHeaders(data ? { "Content-Type": "application/json" } : undefined);
  const res = await fetch(url, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const headers = await buildHeaders();
    const res = await fetch(queryKey.join("/") as string, {
      headers,
      credentials: "include",
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
